import React, { useState } from "react";
import { Tooltip } from "react-tooltip";
import { Info, Printer } from "lucide-react";

import DocIcon from "assets/images/doc.svg";
import { PDFDisplayModal } from "components/Modal/PDFDisplayModal/PDFDisplayModal.jsx";
import { Button } from "components/ui/Button";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { TruncatedText } from "components/ui/TruncatedText/TruncatedText";
import { AllergiesAndIntolerancesAlert } from "features/patients/components/AllergiesAndIntolerancesAlert/AllergiesAndIntolerancesAlert";
import { PatientOverviewDrawer } from "features/patients/components/PatientOverviewDrawer/PatientOverviewDrawer";
import { PregnancyAlert } from "features/patients/components/PregnancyAlert/PregnancyAlert";
import { usePatientData } from "features/patients/hooks/usePatientData";
import {
  formatPatientAge,
  getPatientClassificationLabel,
} from "features/patients/utils";
import { formatDateOfBirth } from "utils/dateFormatter";

import styles from "./PatientCard.module.css";

const PatientCard = () => {
  const { data: patient, isLoading } = usePatientData();
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);

  if (isLoading) {
    return (
      <div
        className={`${styles.patientDetailCard} bg patient-detail-summary-wrap `}
      >
        Loading...
      </div>
    );
  }

  // Everything below reads the patient directly, so there is nothing to draw
  // without one. A failed fetch is reported by the page's own error handling.
  if (!patient) return null;

  const age = formatPatientAge(patient.dateOfBirth);

  // A patient with no address recorded comes back with `address: null`, which
  // is not the same as an absent patient — destructuring it threw (KIN-1678).
  const { city, addressLine1, addressLine2, postCode } = patient.address ?? {};

  return (
    <div
      className={`${styles.patientDetailCard} bg patient-detail-summary-wrap `}
    >
      <div className={styles.patientHeaderWrap}>
        <div className={styles.nameRow}>
          <p className={styles.patientNameSurname}>
            <span className={styles.patientNameTextWrap}>
              {patient.firstName} {patient.surname} ({age})
            </span>
            {patient.died && (
              <span className={styles.deceasedPill}>
                <Pill {...PILL_COLORS.deceased}>Deceased</Pill>
              </span>
            )}
          </p>
          <Button
            variant="text"
            iconButton
            onClick={() => setIsPdfModalOpen(true)}
            aria-label="Print"
          >
            <Printer size={18} />
          </Button>
        </div>
        <p className={styles.patientDob}>
          <span>Born: {formatDateOfBirth(patient.dateOfBirth)}</span>
          <Info
            size={16}
            className={styles.infoIcon}
            data-tooltip-id="patient-card-ids-tooltip"
            data-tooltip-html={`Kinesin Patient ID: ${
              patient.id || "-"
            }<br/>External ID: ${patient.externalPatientId || "-"}`}
          />
        </p>
        <Button
          variant="outline"
          size="large"
          onClick={() => setIsOverviewOpen(true)}
        >
          Patient overview
          <img src={DocIcon} alt="" className={styles.docIcon} />
        </Button>
      </div>

      <AllergiesAndIntolerancesAlert patientId={patient?.id} />

      {/* Kept in step with PatientDetailCard, the fuller card the other tabs use: a pregnancy is
          a fact about the patient, so which tab happens to be open must not decide whether it is
          announced (Eleanor, maternity review). */}
      <PregnancyAlert patientId={patient.id} />

      <div className={styles.patientDetails}>
        <div className={styles.patientBlockWrap}>
          <p className={styles.textManatee}>Medical card number:</p>
          <span className={styles.valueWithClassification}>
            <TruncatedText
              value={patient.medicalCardNumber || "-"}
              className={styles.textStratos}
              tooltipId="patient-card-tooltip"
            />
            <span className={styles.classificationLabel}>
              ({getPatientClassificationLabel(patient.financialClass)})
            </span>
          </span>
        </div>

        <div className={styles.patientBlockWrap}>
          <p className={styles.textManatee}>Phone no:</p>
          <TruncatedText
            value={patient.mobilePhone || patient.homePhone || "-"}
            className={styles.textStratos}
            tooltipId="patient-card-tooltip"
          />
        </div>

        <div className={styles.patientBlockWrap}>
          <p className={styles.textManatee}>Email:</p>
          <TruncatedText
            value={patient.email || "-"}
            className={styles.textStratos}
            tooltipId="patient-card-tooltip"
          />
        </div>

        <div className={styles.patientBlockWrap}>
          <p className={styles.textManatee}>Address:</p>
          <span className={styles.textStratosAddress}>
            {[addressLine1, addressLine2, city && `${city} ${postCode}`]
              .filter(Boolean)
              .join(", ") || "-"}
          </span>
        </div>
      </div>

      <PatientOverviewDrawer
        open={isOverviewOpen}
        onClose={() => setIsOverviewOpen(false)}
      />

      <PDFDisplayModal
        open={isPdfModalOpen}
        onClose={() => setIsPdfModalOpen(false)}
      />

      <Tooltip id="patient-card-tooltip" />
      <Tooltip id="patient-card-ids-tooltip" clickable />
    </div>
  );
};

export { PatientCard };
